import React from 'react';

import { useAppDispatch } from 'redux/hooks/reduxHooks';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputPasswordWrapper } from 'components/Input/InputPassword/InputPasswordWrapper';
import { Stack, Button } from '@mui/material';
import { useChangePasswordMutation } from 'redux/api/auth.api';
import { useLoading } from 'shared/hooks/useLoading';
import { ChangePasswordRequest, TError } from 'shared/types/api.interface';
import { closeCurrentModal } from 'redux/slices/modals/modals.slice';

import {
  CHANGE_PASSWORD_FORM_NAMES,
  CHANGE_PASSWORD_FORM_LABELS,
} from 'shared/constants/form-constants';
import { FORMS_BUTTON } from 'shared/constants/buttons-constants';
import { RESPONSE_CODES } from 'shared/constants/validation-constants';
import { TChangePasswordFormType, ChangePasswordFormValidation } from './validationSchema';

const fieldsArr = [
  CHANGE_PASSWORD_FORM_NAMES.old_password,
  CHANGE_PASSWORD_FORM_NAMES.new_password,
];

export const ChangePasswordForm = () => {
  const dispatch = useAppDispatch();
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  useLoading(isLoading);

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<TChangePasswordFormType>({
    defaultValues: {
      [CHANGE_PASSWORD_FORM_NAMES.old_password]: '',
      [CHANGE_PASSWORD_FORM_NAMES.new_password]: '',
      [CHANGE_PASSWORD_FORM_NAMES.confirm_new_password]: '',
    },
    resolver: zodResolver(ChangePasswordFormValidation),
  });

  const catchSubmitError = (errorsArr: TError[]) => {
    fieldsArr.forEach((field) => {
      setError(field, {
        type: 'custom',
        message:
          errorsArr.find((item: TError) => {
            if (field === CHANGE_PASSWORD_FORM_NAMES.old_password) {
              return item.attr === field || item.code === RESPONSE_CODES.notAthenticated;
            }
            return item.attr === field;
          })?.detail || '',
      });
    });
  };

  const onSubmit: SubmitHandler<Record<string, any>> = async (data: ChangePasswordRequest) => {
    try {
      await changePassword(data).unwrap();
    } catch (err) {
      catchSubmitError(err?.data?.errors);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack
        alignItems="stretch"
        width="100%"
        sx={{ rowGap: { lg: '37px', md: '40px', sm: '40px', xs: '40px' } }}
      >
        <InputPasswordWrapper
          name={CHANGE_PASSWORD_FORM_NAMES.old_password}
          control={control}
          label={CHANGE_PASSWORD_FORM_LABELS.old_password}
          errorMessage={errors.old_password ? (errors.old_password?.message as string) : ''}
        />
        <InputPasswordWrapper
          name={CHANGE_PASSWORD_FORM_NAMES.new_password}
          control={control}
          label={CHANGE_PASSWORD_FORM_LABELS.new_password}
          errorMessage={errors.new_password ? (errors.new_password?.message as string) : ''}
        />
        <InputPasswordWrapper
          name={CHANGE_PASSWORD_FORM_NAMES.confirm_new_password}
          control={control}
          label={CHANGE_PASSWORD_FORM_LABELS.confirm_new_password}
          errorMessage={
            errors.confirm_new_password ? (errors.confirm_new_password?.message as string) : ''
          }
        />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        mt="29px"
        columnGap="25px"
        sx={{ mt: { lg: '56px', md: '40px', sm: '40px', xs: '40px' } }}
      >
        <Button variant="maxSize" type="submit">
          {FORMS_BUTTON.changePasswordConfirmButton}
        </Button>
        <Button
          variant="defaultLight"
          onClick={() => dispatch(closeCurrentModal())}
          sx={{ width: '100%', display: { md: 'flex', sm: 'none', xs: 'none' } }}
        >
          {FORMS_BUTTON.changePasswordCancelButton}
        </Button>
      </Stack>
    </form>
  );
};
